import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, FileImage, FileText, Package, Plus, Trash2, RotateCw, Lock, Sparkles, Pencil, Check, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import JSZip from "jszip";

// ============================================================================
//  CARD STYLES
// ============================================================================
// Fixed premium style presets (theme-independent). "Auto" is computed dynamically
// from the restaurant's own theme so the card matches the public menu look.
const FIXED_STYLES = [
  {
    id: "minimal-black",
    name: { de: "Minimal Schwarz", en: "Minimal Black", tr: "Minimal Siyah" },
    bg: "#0A0A0A",
    text: "#FFFFFF",
    accent: "#D4AF37",
    secondary: "#A0A0A0",
    qrBg: "#FFFFFF",
    qrFg: "#0A0A0A",
    border: "border-zinc-800",
    cardClass: "bg-[#0A0A0A] text-white",
    fontDisplay: "'Playfair Display', serif",
    fontBody: "'Inter', sans-serif",
  },
  {
    id: "wooden",
    name: { de: "Holz Optik", en: "Wooden Café", tr: "Ahşap Görünüm" },
    bg: "#5C3A1F",
    bgGradient:
      "linear-gradient(135deg, #8B5A3C 0%, #6B4423 30%, #4A2E18 70%, #3D2417 100%)",
    text: "#FFF8E7",
    accent: "#F4C26B",
    secondary: "#E8D9B8",
    qrBg: "#FFF8E7",
    qrFg: "#3D2817",
    border: "border-amber-900/40",
    cardClass: "text-amber-50",
    fontDisplay: "'Playfair Display', serif",
    fontBody: "'Inter', sans-serif",
  },
  {
    id: "luxury",
    name: { de: "Luxus Schwarz/Gold", en: "Luxury Black/Gold", tr: "Lüks Siyah/Altın" },
    bg: "#0A0908",
    bgGradient: null,
    text: "#FFFFFF",
    accent: "#D4AF37",
    secondary: "#FFFFFF",
    qrBg: "#FFFFFF",
    qrFg: "#0A0908",
    border: "border-amber-500/50",
    cardClass: "text-white",
    fontDisplay: "'Playfair Display', serif",
    fontBody: "'Inter', sans-serif",
    goldFrame: true,
  },
  {
    id: "neon",
    name: { de: "Neon Modern", en: "Neon Modern", tr: "Neon Modern" },
    bg: "#0A0014",
    bgGradient: "linear-gradient(135deg, #1a0033 0%, #0a0014 50%, #1a002b 100%)",
    text: "#FFFFFF",
    accent: "#FF2EC4",
    secondary: "#00E5FF",
    qrBg: "#0A0014",
    qrFg: "#FF2EC4",
    qrFrame: "#00E5FF",
    border: "border-fuchsia-500/40",
    cardClass: "text-white",
    fontDisplay: "'Audiowide', 'Inter', sans-serif",
    fontBody: "'Inter', sans-serif",
  },
  {
    id: "classic-white",
    name: { de: "Klassisch Weiss", en: "Classic White", tr: "Klasik Beyaz" },
    bg: "#FFFFFF",
    text: "#0A0A0A",
    accent: "#1F2937",
    secondary: "#6B7280",
    qrBg: "#FFFFFF",
    qrFg: "#0A0A0A",
    border: "border-zinc-200",
    cardClass: "bg-white text-zinc-900",
    fontDisplay: "'Playfair Display', serif",
    fontBody: "'Inter', sans-serif",
  },
];

// Compute hex brightness to decide light/dark text
function isHexDark(hex) {
  if (!hex) return true;
  const h = hex.replace("#", "");
  if (h.length < 6) return true;
  const r = parseInt(h.substr(0, 2), 16);
  const g = parseInt(h.substr(2, 2), 16);
  const b = parseInt(h.substr(4, 2), 16);
  return r * 0.299 + g * 0.587 + b * 0.114 < 140;
}

// Build the "Auto" style from the restaurant's actual theme so the card matches
// the customer-facing public menu page (same colors, fonts, branding).
function buildAutoStyle(theme) {
  const primary = theme?.primary_color || "#6366F1";
  const accent = theme?.accent_color || "#A855F7";
  const bg = theme?.background_color || "#FAFAFA";
  const text = theme?.text_color || "#09090B";
  const font = theme?.font_family || "Outfit";
  const dark = isHexDark(bg);
  return {
    id: "auto",
    name: { de: "Restaurant-Theme", en: "Restaurant theme", tr: "Restoran Teması" },
    bg,
    bgGradient: null,
    coverGradient: `linear-gradient(135deg, ${primary}, ${accent})`,
    text,
    accent: primary,
    secondary: dark ? "rgba(255,255,255,0.65)" : "rgba(0,0,0,0.55)",
    qrBg: dark ? "#FFFFFF" : "#FFFFFF",
    qrFg: dark ? "#0A0A0A" : "#0A0A0A",
    border: dark ? "border-white/10" : "border-zinc-200",
    cardClass: "",
    fontDisplay: `'${font}', sans-serif`,
    fontBody: `'${font}', sans-serif`,
    isAuto: true,
  };
}

const INSTRUCTIONS = {
  de: "Menü ansehen",
  en: "Scan to view menu",
  tr: "Menüyü görüntüle",
};

const APPETIT = {
  de: "Guten Appetit!",
  en: "Enjoy your meal!",
  tr: "Afiyet olsun!",
};

const PREFIX_OPTIONS = [
  { value: "Tisch", label_de: "Tisch (DE)", label_en: "Tisch (DE)", label_tr: "Tisch (DE)" },
  { value: "Masa", label_de: "Masa (TR)", label_en: "Masa (TR)", label_tr: "Masa (TR)" },
  { value: "Table", label_de: "Table (EN)", label_en: "Table (EN)", label_tr: "Table (EN)" },
];

// Localized table word — auto translates based on current page language.
// "MASA 1" stored in DB → shown as TISCH/MASA/TABLE depending on lang.
const TABLE_WORD = {
  de: "TISCH",
  en: "TABLE",
  tr: "MASA",
};

// Parse a label like "Tisch 12" / "Masa 1" / "VIP Lounge"
// → For numbered labels: { isNumbered: true, number: "12" }
// → For custom names like "VIP Lounge": { isNumbered: false, custom: "VIP Lounge" }
function parseTableLabel(label, fallbackNumber) {
  if (!label) return { isNumbered: true, number: String(fallbackNumber ?? "") };
  const m = label.trim().match(/^(.+?)\s+(\d+)\s*$/);
  if (m) return { isNumbered: true, number: m[2] };
  return { isNumbered: false, custom: label };
}

// ============================================================================
//  ACRYLIC TABLE STAND MOCKUP — wraps the printable card to look like a real
//  acrylic display sitting on a wooden base, against a blurred restaurant.
// ============================================================================
function AcrylicStandMockup({ children, size = 320 }) {
  const baseH = size * 0.18;
  return (
    <div
      className="relative rounded-3xl overflow-hidden"
      style={{
        width: size * 1.45,
        padding: 0,
      }}
      data-testid="acrylic-stand-mockup"
    >
      {/* Restaurant bokeh background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&q=70&blur=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(8px) brightness(0.7) saturate(1.1)",
          transform: "scale(1.15)",
        }}
      />
      {/* Warm vignette */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 50% 60%, rgba(244,196,107,0.15) 0%, rgba(0,0,0,0.55) 70%)",
        }}
      />

      {/* Stand */}
      <div className="relative flex flex-col items-center justify-end" style={{ paddingTop: size * 0.18, paddingBottom: size * 0.06 }}>
        {/* Acrylic frame: highlight edges + soft transparency around card */}
        <div
          className="relative"
          style={{
            padding: size * 0.025,
            borderRadius: 28,
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 40%, rgba(255,255,255,0.10) 60%, rgba(255,255,255,0.22) 100%)",
            backdropFilter: "blur(2px)",
            boxShadow:
              "0 0 0 1px rgba(255,255,255,0.25), 0 30px 60px rgba(0,0,0,0.55), 0 0 80px rgba(255,180,80,0.10)",
          }}
        >
          {/* Bright vertical edge highlights (acrylic effect) */}
          <div
            className="absolute pointer-events-none"
            style={{
              top: 6,
              bottom: 6,
              left: 2,
              width: 2,
              borderRadius: 2,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,0.85) 50%, rgba(255,255,255,0.6) 75%, rgba(255,255,255,0) 100%)",
              filter: "blur(0.5px)",
            }}
          />
          <div
            className="absolute pointer-events-none"
            style={{
              top: 6,
              bottom: 6,
              right: 2,
              width: 2,
              borderRadius: 2,
              background:
                "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 25%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0.4) 75%, rgba(255,255,255,0) 100%)",
              filter: "blur(0.5px)",
            }}
          />
          {children}
        </div>

        {/* Wooden base */}
        <div
          className="relative"
          style={{
            width: size * 1.05,
            height: baseH,
            marginTop: -baseH * 0.15,
            borderRadius: "6px 6px 14px 14px",
            background:
              "linear-gradient(180deg, #B8855F 0%, #8B5A3C 35%, #6B4423 75%, #4A2E18 100%)",
            boxShadow:
              "0 20px 30px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,210,170,0.5), inset 0 -3px 8px rgba(0,0,0,0.4)",
          }}
        >
          {/* Wood grain lines */}
          <div
            className="absolute inset-0 pointer-events-none opacity-30"
            style={{
              backgroundImage:
                "repeating-linear-gradient(90deg, transparent 0, transparent 8px, rgba(0,0,0,0.18) 8px, rgba(0,0,0,0.18) 9px, transparent 9px, transparent 20px)",
              borderRadius: "inherit",
            }}
          />
          {/* Top reflective edge of wood */}
          <div
            className="absolute top-0 left-1 right-1 h-1.5 rounded-t-sm"
            style={{
              background: "linear-gradient(180deg, rgba(255,220,180,0.6) 0%, transparent 100%)",
            }}
          />
        </div>

        {/* Table surface shadow */}
        <div
          className="absolute pointer-events-none"
          style={{
            bottom: size * 0.02,
            left: "50%",
            transform: "translateX(-50%)",
            width: size * 1.25,
            height: size * 0.04,
            borderRadius: "50%",
            background: "radial-gradient(ellipse, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)",
            filter: "blur(4px)",
          }}
        />
      </div>
    </div>
  );
}

// Premium steaming-coffee SVG icon — replaces the lucide Coffee icon for a
// more elegant restaurant look (matches the reference card hero design).
function SteamCoffeeIcon({ size: s = 40, color = "#D4AF37" }) {
  return (
    <svg width={s} height={s * 1.05} viewBox="0 0 64 68" fill="none">
      {/* Steam */}
      <path d="M22 4 C20 8, 24 10, 22 14 C20 18, 24 20, 22 24" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.85" />
      <path d="M32 2 C30 7, 34 9, 32 13 C30 17, 34 19, 32 23" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M42 4 C40 8, 44 10, 42 14 C40 18, 44 20, 42 24" stroke={color} strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.85" />
      {/* Cup body */}
      <path d="M12 30 L52 30 L48 56 C47 62, 43 64, 38 64 L26 64 C21 64, 17 62, 16 56 Z" fill={color} />
      {/* Handle */}
      <path d="M52 36 C58 36, 60 42, 60 46 C60 50, 58 56, 52 56" stroke={color} strokeWidth="3" strokeLinecap="round" fill="none" />
      {/* Saucer line */}
      <ellipse cx="32" cy="65" rx="22" ry="2" fill={color} opacity="0.6" />
    </svg>
  );
}

// ============================================================================
//  CARD COMPONENT (renders ONE printable card)
// ============================================================================
function PrintableCard({ style, tableNumber, tableLabel, restaurantName, restaurantLogo, qrUrl, instruction, appetit, langCode = "de", size = 380 }) {
  const isLight = style.id === "classic-white";
  const bgStyle = style.bgImage
    ? { backgroundImage: style.bgImage, backgroundSize: "cover", backgroundPosition: "center" }
    : style.bgGradient
    ? { background: style.bgGradient }
    : { background: style.bg };

  const isNeon = style.id === "neon";
  const isLuxury = style.id === "luxury";

  // Sizing relative to card width — taller to ensure ALL elements fit: steam, name, dot, TISCH, number, dot, QR, scan, appetit, line+heart+line
  const aspect = 1.30;
  const pad = size * 0.05;
  const nameFont = size * 0.055;
  const tischFont = size * 0.056;
  const numFont = size * 0.16;
  const instrFont = size * 0.034;
  const appetitFont = size * 0.072;
  const qrSize = size * 0.38;

  // Parse label → number (or treat whole label as the "name" for custom)
  const parsed = parseTableLabel(tableLabel, tableNumber);
  const localizedWord = TABLE_WORD[langCode] || TABLE_WORD.de;
  const showPrefix = parsed.isNumbered;
  const displayNumber = parsed.isNumbered ? parsed.number : parsed.custom;
  const customNumFont = displayNumber.length > 4 ? numFont * 0.45 : displayNumber.length > 2 ? numFont * 0.7 : numFont;

  // Just a tiny gold dot (matches reference — between Lezzet Café & TISCH, and between 12 & QR).
  const Dot = ({ opacity = 0.95 }) => (
    <div
      style={{
        width: size * 0.013,
        height: size * 0.013,
        borderRadius: 999,
        background: style.accent,
        opacity,
      }}
    />
  );

  // Decorative divider (line • dot • line) — used only at the bottom ornament now.
  const Divider = ({ width = size * 0.18, opacity = 0.85 }) => (
    <div className="flex items-center justify-center" style={{ gap: size * 0.012 }}>
      <span style={{ width, height: 1, background: style.accent, opacity }} />
      <span style={{ width: size * 0.012, height: size * 0.012, borderRadius: 999, background: style.accent, opacity }} />
      <span style={{ width, height: 1, background: style.accent, opacity }} />
    </div>
  );

  return (
    <div
      className={`relative rounded-3xl overflow-hidden ${style.cardClass}`}
      style={{
        ...bgStyle,
        width: size,
        height: size * aspect,
        fontFamily: style.fontBody,
        boxShadow: isNeon
          ? "0 0 60px rgba(255,46,196,0.35), 0 0 30px rgba(0,229,255,0.25), 0 25px 50px rgba(0,0,0,0.5)"
          : isLuxury
          ? "0 30px 70px rgba(0,0,0,0.65), 0 0 0 0.5px rgba(212,175,55,0.2)"
          : "0 25px 50px rgba(0,0,0,0.3)",
      }}
    >
      {/* LUXURY: single thin gold border frame (matches reference exactly) */}
      {isLuxury && (
        <div
          className="absolute pointer-events-none"
          style={{
            top: size * 0.028,
            left: size * 0.028,
            right: size * 0.028,
            bottom: size * 0.028,
            borderRadius: 20,
            border: `1px solid ${style.accent}`,
            opacity: 0.95,
          }}
        />
      )}

      {/* Auto-style: gradient cover strip at top */}
      {style.isAuto && style.coverGradient && (
        <div
          className="absolute top-0 left-0 right-0 pointer-events-none"
          style={{
            height: size * 0.16,
            background: style.coverGradient,
            borderTopLeftRadius: 24,
            borderTopRightRadius: 24,
            opacity: 0.95,
          }}
        />
      )}

      <div
        className="relative h-full flex flex-col items-center text-center"
        style={{ paddingLeft: pad * 1.4, paddingRight: pad * 1.4, paddingTop: pad * 1.0, paddingBottom: pad * 0.8, gap: size * 0.006 }}
      >
        {/* === TOP: Steam coffee icon + Restaurant name + divider === */}
        <div className="flex flex-col items-center" style={{ gap: size * 0.018 }}>
          {restaurantLogo ? (
            <img
              src={restaurantLogo}
              alt=""
              className="object-contain rounded-md"
              style={{
                width: size * 0.16,
                height: size * 0.16,
                marginTop: style.isAuto ? size * 0.04 : 0,
                boxShadow: style.isAuto ? `0 0 0 3px ${style.bg}, 0 0 0 5px ${style.accent}40` : "none",
              }}
              crossOrigin="anonymous"
            />
          ) : (
            <SteamCoffeeIcon size={size * 0.10} color={isLight ? style.accent : style.accent} />
          )}
          <div
            className="font-semibold"
            style={{
              fontSize: nameFont,
              fontFamily: style.fontDisplay,
              color: isNeon ? style.accent : style.text,
              textShadow: isNeon ? `0 0 10px ${style.accent}` : "none",
              letterSpacing: isLuxury ? "0.02em" : "0.03em",
              lineHeight: 1.1,
            }}
          >
            {restaurantName}
          </div>
          <Dot opacity={0.95} />
        </div>

        {/* === MIDDLE: TISCH / 12 === */}
        <div className="flex flex-col items-center" style={{ marginTop: size * 0.01, gap: size * 0.005 }}>
          {showPrefix && (
            <div
              className="uppercase"
              style={{
                fontSize: tischFont,
                fontFamily: style.fontDisplay,
                color: style.accent,
                letterSpacing: "0.28em",
                fontWeight: 600,
              }}
            >
              {localizedWord}
            </div>
          )}
          <div
            className="leading-none"
            style={{
              fontSize: showPrefix ? numFont : customNumFont,
              fontFamily: style.fontDisplay,
              color: isNeon ? style.accent : style.text,
              textShadow: isNeon ? `0 0 18px ${style.accent}, 0 0 36px ${style.accent}80` : "none",
              fontWeight: 700,
              textAlign: "center",
              wordBreak: "break-word",
              maxWidth: size * 0.85,
              letterSpacing: showPrefix ? "0.01em" : "0.04em",
              marginTop: showPrefix ? size * 0.005 : size * 0.015,
            }}
          >
            {displayNumber}
          </div>
          <div style={{ marginTop: size * 0.018 }}>
            <Dot opacity={0.95} />
          </div>
        </div>

        {/* === QR CODE === */}
        <div
          className="rounded-md grid place-items-center"
          style={{
            background: style.qrBg,
            padding: size * 0.02,
            marginTop: size * 0.005,
            boxShadow: isNeon ? `0 0 24px ${style.qrFrame}80, inset 0 0 0 2px ${style.qrFrame}` : "none",
          }}
          data-qr-host="true"
        >
          <QRCodeSVG
            value={qrUrl}
            size={qrSize}
            level="H"
            includeMargin={false}
            fgColor={style.qrFg}
            bgColor={style.qrBg}
          />
        </div>

        {/* === Instruction: 📱 + "Scan to view menu" === */}
        <div className="flex items-center" style={{ gap: size * 0.018, marginTop: size * 0.012 }}>
          <div
            className="grid place-items-center"
            style={{
              width: size * 0.04,
              height: size * 0.058,
              border: `1px solid ${style.accent}`,
              borderRadius: 3,
            }}
          >
            <Smartphone size={size * 0.025} color={style.accent} />
          </div>
          <div
            style={{
              fontSize: instrFont,
              color: isLuxury ? style.text : style.secondary,
              letterSpacing: "0.04em",
              fontWeight: 400,
            }}
          >
            {instruction}
          </div>
        </div>

        {/* === Appetit: gold script === */}
        <div
          className="leading-none"
          style={{
            fontSize: appetitFont,
            fontFamily: "'Great Vibes', cursive",
            color: style.accent,
            marginTop: size * 0.005,
          }}
        >
          {appetit}
        </div>

        {/* === Bottom ornament: long line + heart + long line (matches reference) === */}
        <div className="flex items-center justify-center mt-auto" style={{ gap: size * 0.022, paddingTop: size * 0.018 }}>
          <span style={{ flex: 1, maxWidth: size * 0.32, height: 1, background: style.accent, opacity: 0.7 }} />
          <span style={{ color: style.accent, fontSize: size * 0.034, lineHeight: 1 }}>♥</span>
          <span style={{ flex: 1, maxWidth: size * 0.32, height: 1, background: style.accent, opacity: 0.7 }} />
        </div>
      </div>
    </div>
  );
}

// ============================================================================
//  MAIN PAGE
// ============================================================================
export default function TableCards() {
  const { id } = useParams();
  const { lang } = useLang();
  const { user } = useAuth();

  const [restaurant, setRestaurant] = useState(null);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);

  const [styleId, setStyleId] = useState("luxury");
  const [activeIdx, setActiveIdx] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  // Bulk create
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkPrefix, setBulkPrefix] = useState("Tisch");
  const [bulkStart, setBulkStart] = useState(1);

  // Single add
  const [singleLabel, setSingleLabel] = useState("");

  const previewRef = useRef(null);
  const isPro = user?.role === "admin" || ["pro", "premium", "agency"].includes(user?.plan);

  const tx = (k) =>
    ({
      title: { de: "Tisch QR-Karten", en: "Table QR Cards", tr: "Masa QR Kartları" },
      subtitle: {
        de: "Erstelle premium QR-Tischaufsteller — bis zu 100 Tische pro Restaurant.",
        en: "Generate premium QR table stands — up to 100 tables per restaurant.",
        tr: "Premium QR masa standları oluşturun — restoran başına 100 masaya kadar.",
      },
      bulk: { de: "Mehrere Tische auf einmal", en: "Bulk create tables", tr: "Toplu masa oluştur" },
      bulkDesc: {
        de: "Erstelle z.B. 25 Tische sofort mit fortlaufender Nummerierung.",
        en: "Spin up e.g. 25 tables instantly with sequential numbering.",
        tr: "Örneğin 25 masayı sıralı numaralarla anında oluşturun.",
      },
      count: { de: "Anzahl", en: "Count", tr: "Adet" },
      prefix: { de: "Bezeichnung", en: "Prefix", tr: "Önek" },
      startNum: { de: "Start ab", en: "Start at", tr: "Başlangıç" },
      create: { de: "Erstellen", en: "Create", tr: "Oluştur" },
      addOne: { de: "Einzeln hinzufügen", en: "Add single", tr: "Tek ekle" },
      tableLabel: { de: "Tisch-Name (z.B. VIP Lounge)", en: "Table name (e.g. VIP Lounge)", tr: "Masa adı (örn. VIP Lounge)" },
      yourTables: { de: "Deine Tische", en: "Your tables", tr: "Masalarınız" },
      noTables: { de: "Noch keine Tische. Lege oben los!", en: "No tables yet. Start above!", tr: "Henüz masa yok. Yukarıdan başlayın!" },
      style: { de: "Karten-Stil", en: "Card style", tr: "Kart stili" },
      preview: { de: "Vorschau", en: "Preview", tr: "Önizleme" },
      downloadPng: { de: "PNG", en: "PNG", tr: "PNG" },
      downloadPdf: { de: "PDF", en: "PDF", tr: "PDF" },
      downloadAllZip: { de: "Alle als ZIP", en: "All as ZIP", tr: "Tümü ZIP" },
      downloadAllPdf: { de: "Druckbares PDF (alle)", en: "Printable PDF (all)", tr: "Yazdırılabilir PDF (tümü)" },
      regenerate: { de: "QR neu generieren", en: "Regenerate QR", tr: "QR yenile" },
      rename: { de: "Umbenennen", en: "Rename", tr: "Yeniden adlandır" },
      del: { de: "Löschen", en: "Delete", tr: "Sil" },
      scans: { de: "Scans", en: "scans", tr: "tarama" },
      saved: { de: "Gespeichert", en: "Saved", tr: "Kaydedildi" },
      added: { de: "Hinzugefügt", en: "Added", tr: "Eklendi" },
      proRequired: { de: "Pro-Plan erforderlich", en: "Pro plan required", tr: "Pro plan gerekli" },
      proRequiredDesc: {
        de: "Tisch-Karten sind ein Pro-Feature. Jetzt freischalten.",
        en: "Table cards are a Pro feature. Unlock now.",
        tr: "Masa kartları Pro özelliğidir. Şimdi açın.",
      },
      upgrade: { de: "Jetzt upgraden", en: "Upgrade now", tr: "Şimdi yükselt" },
      limitReached: { de: "Maximal 100 Tische pro Restaurant", en: "Max 100 tables per restaurant", tr: "Restoran başına en fazla 100 masa" },
      confirmDelete: { de: "Wirklich löschen?", en: "Delete this table?", tr: "Bu masayı silmek istiyor musunuz?" },
      confirmRegen: {
        de: "Neuer QR-Code wird erzeugt. Alte gedruckte Karten werden ungültig. Fortfahren?",
        en: "A new QR will be generated. Old printed cards will become invalid. Continue?",
        tr: "Yeni bir QR oluşturulacak. Eski basılı kartlar geçersiz olacak. Devam?",
      },
    }[k]?.[lang] || "");

  // Fetch data
  useEffect(() => {
    const load = async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get(`/restaurants/${id}`),
          isPro ? api.get(`/restaurants/${id}/tables`) : Promise.resolve({ data: [] }),
        ]);
        setRestaurant(r1.data);
        setTables(r2.data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isPro]);

  // Dynamic style list — Luxury black/gold first (premium default),
  // followed by other premium presets, Auto (restaurant theme) last.
  const STYLES = useMemo(() => {
    const luxury = FIXED_STYLES.find((s) => s.id === "luxury");
    const others = FIXED_STYLES.filter((s) => s.id !== "luxury");
    const autoStyle = buildAutoStyle(restaurant?.theme);
    return [luxury, ...others, autoStyle];
  }, [restaurant?.theme]);

  const style = useMemo(() => STYLES.find((s) => s.id === styleId) || STYLES[0], [styleId, STYLES]);
  const baseUrl = restaurant ? `${window.location.origin}/m/${restaurant.slug}` : "";
  const activeTable = tables[activeIdx] || null;
  const activeUrl = activeTable ? `${baseUrl}?table=${activeTable.id}` : baseUrl;

  // ----- Actions -----
  const refresh = async () => {
    const r = await api.get(`/restaurants/${id}/tables`);
    setTables(r.data || []);
  };

  const addSingle = async (e) => {
    e.preventDefault();
    if (!singleLabel.trim()) return;
    try {
      await api.post(`/restaurants/${id}/tables`, { label: singleLabel.trim() });
      setSingleLabel("");
      toast.success(tx("added"));
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const bulkCreate = async (e) => {
    e.preventDefault();
    const c = parseInt(bulkCount, 10);
    if (!c || c < 1) return;
    if (tables.length + c > 100) {
      toast.error(tx("limitReached"));
      return;
    }
    try {
      await api.post(`/restaurants/${id}/tables/bulk`, {
        count: c,
        prefix: bulkPrefix,
        start: parseInt(bulkStart, 10) || 1,
      });
      toast.success(`${c} ${tx("added")}`);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const startRename = (tb) => {
    setEditingId(tb.id);
    setEditLabel(tb.label);
  };

  const saveRename = async (tb) => {
    try {
      await api.patch(`/tables/${tb.id}`, { label: editLabel });
      setEditingId(null);
      toast.success(tx("saved"));
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const regenerate = async (tb) => {
    if (!window.confirm(tx("confirmRegen"))) return;
    try {
      await api.post(`/tables/${tb.id}/regenerate`);
      toast.success(tx("saved"));
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const deleteTb = async (tb) => {
    if (!window.confirm(tx("confirmDelete"))) return;
    await api.delete(`/tables/${tb.id}`);
    toast.success("✓");
    await refresh();
    if (activeIdx >= tables.length - 1) setActiveIdx(0);
  };

  // ----- Downloads -----
  // Wait until web fonts are ready so html2canvas captures premium typography.
  const ensureFontsReady = async () => {
    try {
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
        // Force-load specific weights/families we use on the card
        const families = [
          '700 24px "Playfair Display"',
          '900 64px "Playfair Display"',
          '700 24px "Cinzel"',
          '400 24px "Great Vibes"',
          '400 24px "Audiowide"',
        ];
        await Promise.all(families.map((f) => document.fonts.load(f).catch(() => null)));
      }
    } catch (e) { /* noop */ }
  };

  const captureCard = async (node) => {
    await ensureFontsReady();
    // Render at higher scale for print sharpness
    const canvas = await html2canvas(node, {
      backgroundColor: null,
      scale: 3,
      useCORS: true,
      allowTaint: true,
      logging: false,
      imageTimeout: 0,
    });
    return canvas;
  };

  const downloadPng = async () => {
    if (!previewRef.current) {
      toast.error("Preview not ready");
      return;
    }
    const card = previewRef.current.querySelector("[data-card]");
    if (!card) {
      toast.error("No card to download");
      return;
    }
    try {
      toast.info("Rendering PNG…");
      const canvas = await captureCard(card);
      canvas.toBlob((blob) => {
        if (!blob) { toast.error("PNG render failed"); return; }
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const safeName = (activeTable?.label || "main").replace(/\s+/g, "_");
        a.download = `${restaurant.slug}-${safeName}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success("PNG ✓");
      }, "image/png");
    } catch (err) {
      console.error("PNG download error:", err);
      toast.error("Download failed: " + (err.message || "unknown"));
    }
  };

  const downloadPdf = async () => {
    if (!previewRef.current) {
      toast.error("Preview not ready");
      return;
    }
    const card = previewRef.current.querySelector("[data-card]");
    if (!card) {
      toast.error("No card to download");
      return;
    }
    try {
      toast.info("Rendering PDF…");
      const canvas = await captureCard(card);
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const w = 140;
      const h = w * (canvas.height / canvas.width);
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", (pageW - w) / 2, (pageH - h) / 2, w, h);
      pdf.save(`${restaurant.slug}-${(activeTable?.label || "main").replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF ✓");
    } catch (err) {
      console.error("PDF download error:", err);
      toast.error("Download failed: " + (err.message || "unknown"));
    }
  };

  // Off-screen renderer for batch — always uses the current page language.
  const renderTableCardCanvas = async (tb) => {
    const host = document.createElement("div");
    host.style.position = "fixed";
    host.style.left = "-9999px";
    host.style.top = "0";
    host.style.zIndex = "-1";
    document.body.appendChild(host);

    const { createRoot } = await import("react-dom/client");
    const root = createRoot(host);
    await new Promise((resolve) => {
      root.render(
        <PrintableCard
          style={style}
          tableNumber={tb.number ?? "?"}
          tableLabel={tb.label}
          restaurantName={restaurant.name}
          restaurantLogo={restaurant.logo}
          qrUrl={`${baseUrl}?table=${tb.id}`}
          instruction={INSTRUCTIONS[lang]}
          appetit={APPETIT[lang]}
          langCode={lang}
          size={520}
        />
      );
      setTimeout(resolve, 400);
    });
    const target = host.querySelector("div");
    const canvas = await captureCard(target);
    root.unmount();
    document.body.removeChild(host);
    return canvas;
  };

  const downloadAllZip = async () => {
    if (tables.length === 0) return;
    try {
      toast.info(`Rendering ${tables.length} cards…`);
      const zip = new JSZip();
      for (let i = 0; i < tables.length; i++) {
        const tb = tables[i];
        const canvas = await renderTableCardCanvas(tb);
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
        zip.file(`${restaurant.slug}-${tb.label.replace(/\s+/g, "_")}.png`, blob);
      }
      const out = await zip.generateAsync({ type: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(out);
      a.download = `${restaurant.slug}-table-qr-cards.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("ZIP ✓");
    } catch (err) {
      console.error("ZIP error:", err);
      toast.error("Download failed: " + (err.message || "unknown"));
    }
  };

  const downloadAllPdf = async () => {
    if (tables.length === 0) return;
    try {
      toast.info(`Rendering ${tables.length} cards…`);
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const cardW = 150;
      for (let i = 0; i < tables.length; i++) {
        const tb = tables[i];
        const canvas = await renderTableCardCanvas(tb);
        const cardH = cardW * (canvas.height / canvas.width);
        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL("image/png"), "PNG", (pageW - cardW) / 2, (pageH - cardH) / 2, cardW, cardH);
      }
      pdf.save(`${restaurant.slug}-all-tables.pdf`);
      toast.success("PDF ✓");
    } catch (err) {
      console.error("PDF (all) error:", err);
      toast.error("Download failed: " + (err.message || "unknown"));
    }
  };

  // ----- Render -----
  if (loading) {
    return (
      <DashboardLayout restaurantId={id}>
        <div className="text-muted-foreground">Loading…</div>
      </DashboardLayout>
    );
  }

  if (!isPro) {
    return (
      <DashboardLayout restaurantId={id}>
        <div className="rounded-3xl border border-dashed border-purple-500/30 bg-purple-500/5 p-12 text-center" data-testid="pro-gate">
          <Lock className="h-10 w-10 mx-auto text-purple-400 mb-4" />
          <h2 className="text-2xl font-bold">{tx("proRequired")}</h2>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">{tx("proRequiredDesc")}</p>
          <Link to="/pricing">
            <Button className="mt-6 rounded-full bg-purple-600 hover:bg-purple-700 text-white">
              <Sparkles className="h-4 w-4 mr-2" />
              {tx("upgrade")}
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout restaurantId={id}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{tx("title")}</div>
            <h1 className="text-3xl font-bold tracking-tight">{tx("title")}</h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">{tx("subtitle")}</p>
          </div>
          <Link
            to={`/dashboard/restaurants/${id}/qr`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary hover:bg-secondary/80 text-sm font-semibold transition-colors"
            data-testid="link-back-to-qr"
          >
            <ArrowLeft className="h-4 w-4" />
            {lang === "de" ? "Zurück zu QR-Code" : lang === "tr" ? "QR koda dön" : "Back to QR code"}
          </Link>
        </div>

        {/* Bulk + Single Create */}
        <div className="grid lg:grid-cols-3 gap-4">
          <form onSubmit={bulkCreate} className="rounded-3xl border bg-card p-5 lg:col-span-2 space-y-4" data-testid="bulk-create-card">
            <div>
              <h3 className="font-bold">{tx("bulk")}</h3>
              <p className="text-xs text-muted-foreground mt-1">{tx("bulkDesc")}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">{tx("count")}</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={bulkCount}
                  onChange={(e) => setBulkCount(e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="bulk-count"
                />
              </div>
              <div>
                <Label className="text-xs">{tx("prefix")}</Label>
                <select
                  value={bulkPrefix}
                  onChange={(e) => setBulkPrefix(e.target.value)}
                  className="w-full mt-1 h-10 px-3 rounded-xl border bg-background text-sm"
                  data-testid="bulk-prefix"
                >
                  {PREFIX_OPTIONS.map((p) => (
                    <option key={p.value} value={p.value}>{p[`label_${lang}`] || p.value}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">{tx("startNum")}</Label>
                <Input
                  type="number"
                  min="1"
                  value={bulkStart}
                  onChange={(e) => setBulkStart(e.target.value)}
                  className="rounded-xl mt-1"
                  data-testid="bulk-start"
                />
              </div>
            </div>
            <Button type="submit" className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="bulk-create-btn">
              <Plus className="h-4 w-4 mr-2" />
              {tx("create")} ({tables.length}/100)
            </Button>
          </form>

          <form onSubmit={addSingle} className="rounded-3xl border bg-card p-5 space-y-3" data-testid="single-add-card">
            <h3 className="font-bold">{tx("addOne")}</h3>
            <p className="text-xs text-muted-foreground">VIP Tisch · Garten 1 · Lounge A</p>
            <Input
              placeholder={tx("tableLabel")}
              value={singleLabel}
              onChange={(e) => setSingleLabel(e.target.value)}
              className="rounded-xl"
              data-testid="single-label-input"
            />
            <Button type="submit" className="w-full rounded-full" variant="outline" data-testid="single-add-btn">
              <Plus className="h-4 w-4 mr-2" />
              {tx("create")}
            </Button>
          </form>
        </div>

        {/* Style selector — real mini-card thumbnails */}
        <div className="rounded-3xl border bg-card p-5 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-bold">{tx("style")}</h3>
              <p className="text-xs text-muted-foreground">
                {lang === "de"
                  ? "Wähle einen Stil — Live-Vorschau rechts."
                  : lang === "tr"
                  ? "Bir stil seçin — sağda canlı önizleme."
                  : "Pick a style — live preview on the right."}
              </p>
            </div>
            <div className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400">
              <Sparkles className="h-3 w-3" />
              {lang === "de" ? "Sprache folgt dem Panel" : lang === "tr" ? "Dil panele bağlı" : "Language follows panel"}: <strong className="uppercase">{lang}</strong>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {STYLES.map((s) => (
              <button
                key={s.id}
                onClick={() => setStyleId(s.id)}
                className={`rounded-2xl border-2 p-2 text-left transition-all ${
                  styleId === s.id ? "border-amber-500 ring-2 ring-amber-500/30 scale-[1.02]" : "border-transparent hover:border-amber-400/40"
                }`}
                data-testid={`style-${s.id}`}
                title={s.name[lang] || s.name.en}
              >
                <div className="rounded-xl overflow-hidden bg-zinc-50 dark:bg-zinc-900 p-2 grid place-items-center min-h-[210px]">
                  {restaurant ? (
                    <div style={{ transform: "scale(0.92)" }}>
                      <PrintableCard
                        style={s}
                        tableNumber={(activeTable?.number ?? activeIdx + 1)}
                        tableLabel={activeTable?.label || `${bulkPrefix} 1`}
                        restaurantName={restaurant.name}
                        restaurantLogo={restaurant.logo}
                        qrUrl={activeUrl || baseUrl}
                        instruction={INSTRUCTIONS[lang]}
                        appetit={APPETIT[lang]}
                        langCode={lang}
                        size={170}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-32" />
                  )}
                </div>
                <div className="mt-2 px-1 text-center">
                  <div className="text-xs font-bold truncate">{s.name[lang] || s.name.en}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Tables list + preview */}
        <div className="grid lg:grid-cols-[1fr_420px] gap-6">
          {/* List */}
          <div className="rounded-3xl border bg-card p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-bold">{tx("yourTables")} <span className="text-muted-foreground text-sm">({tables.length}/100)</span></h3>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" onClick={downloadAllZip} disabled={tables.length === 0} variant="outline" className="rounded-full" data-testid="download-zip-btn">
                  <Package className="h-3.5 w-3.5 mr-2" />
                  ZIP
                </Button>
                <Button size="sm" onClick={downloadAllPdf} disabled={tables.length === 0} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="download-all-pdf-btn">
                  <FileText className="h-3.5 w-3.5 mr-2" />
                  PDF
                </Button>
              </div>
            </div>
            {tables.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground text-sm">{tx("noTables")}</div>
            ) : (
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                {tables.map((tb, i) => (
                  <div
                    key={tb.id}
                    className={`rounded-2xl border p-3 flex items-center gap-3 cursor-pointer transition-all ${
                      i === activeIdx ? "border-purple-500 bg-purple-500/5" : "hover:border-purple-300"
                    }`}
                    onClick={() => setActiveIdx(i)}
                    data-testid={`table-row-${tb.id}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-zinc-900 grid place-items-center text-white font-bold text-lg shrink-0">
                      {tb.number ?? "—"}
                    </div>
                    <div className="flex-1 min-w-0">
                      {editingId === tb.id ? (
                        <div className="flex gap-1">
                          <Input
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-8 rounded-lg"
                            data-testid={`edit-input-${tb.id}`}
                          />
                          <Button size="sm" variant="outline" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); saveRename(tb); }}>
                            <Check className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={(e) => { e.stopPropagation(); setEditingId(null); }}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <>
                          <div className="font-semibold text-sm truncate">{tb.label}</div>
                          <div className="text-[10px] text-muted-foreground font-mono">#{tb.id} · {tb.scan_count || 0} {tx("scans")}</div>
                        </>
                      )}
                    </div>
                    {editingId !== tb.id && (
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); startRename(tb); }} title={tx("rename")} data-testid={`rename-${tb.id}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); regenerate(tb); }} title={tx("regenerate")} data-testid={`regen-${tb.id}`}>
                          <RotateCw className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:text-rose-500" onClick={(e) => { e.stopPropagation(); deleteTb(tb); }} title={tx("del")} data-testid={`delete-${tb.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="space-y-3" ref={previewRef}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold text-center">{tx("preview")}</div>
            <div className="grid place-items-center rounded-3xl overflow-hidden bg-zinc-950 p-8" data-testid="preview-container">
              {tables.length === 0 ? (
                <div className="text-sm text-muted-foreground py-20 text-center">{tx("noTables")}</div>
              ) : (
                <div data-card>
                  <PrintableCard
                    style={style}
                    tableNumber={activeTable?.number ?? activeIdx + 1}
                    tableLabel={activeTable?.label}
                    restaurantName={restaurant.name}
                    restaurantLogo={restaurant.logo}
                    qrUrl={activeUrl}
                    instruction={INSTRUCTIONS[lang]}
                    appetit={APPETIT[lang]}
                    langCode={lang}
                    size={340}
                  />
                </div>
              )}
            </div>
            {tables.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={downloadPng} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="card-png-btn">
                  <FileImage className="h-4 w-4 mr-2" />
                  {tx("downloadPng")}
                </Button>
                <Button onClick={downloadPdf} variant="outline" className="rounded-full" data-testid="card-pdf-btn">
                  <FileText className="h-4 w-4 mr-2" />
                  {tx("downloadPdf")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Web fonts for premium typography */}
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Cinzel:wght@500;700&family=Great+Vibes&family=Audiowide&display=swap" rel="stylesheet" />
    </DashboardLayout>
  );
}
